import React, { useEffect } from 'react'
import { Box, Card, Divider } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import {
  cancelEditingOrder,
  loadEditOrder,
  removeItemFromCheckoutCartStart,
} from '../../redux/modules/cart'
import { clearMenuItem, selectMenuItem } from '../../redux/modules/order'
import {
  selectCurrentLocationWithAddress,
  selectMenuItemForEdit,
} from '../../redux/modules/selectors'
import { placeOrderRoute } from '../../routes/routes'
import { formatUrl } from '../../util/formatUtils'
import { pushSnowplow, toOrderProcessSchema } from '../../util/snowplow'
import DeliveryDetails from '../common/DeliveryDetails'
import { CheckoutCart } from '../cart/Cart'
import Captcha from '../common/Captcha'
import MapPreview from '../common/MapPreview'
import PersonalizeOrder from '../personalize-order/PersonalizeOrder'
import ButtonCheckout from '../place-order/ButtonCheckout'
import CheckoutError from './CheckoutError'

const useStyles = makeStyles({
  card: {
    padding: '0',
  },
})

const CheckoutSidebar = ({
  checkoutCart,
  checkoutCartError,
  errorCheckingOut,
  couponUsage,
  creditUsage,
  deliveryDropoff,
  isAvailableCreditsLoading,
  isCheckoutCartLoading,
  isDeliveryDropoffLoading,
  isCartSaving,
  isCheckoutProcessing,
  isEditingOrder,
  isLoadingOrderForEdit,
  location,
  menuItemForEdit,
  normalizedMenuItem,
  programUsage,
  storedCurrencyUsage,
  removeItemFromCheckoutCartStart,
  selectMenuItem,
  clearMenuItem,
  loadEditOrder,
  cancelEditingOrder,
}) => {
  const classes = useStyles()

  const handleClose = () => {
    clearMenuItem()
    cancelEditingOrder()
  }

  useEffect(() => {
    if (errorCheckingOut) {
      pushSnowplow(toOrderProcessSchema('daily', 'checkout', undefined, errorCheckingOut))
    }
  }, [errorCheckingOut])

  const hasNoItem = checkoutCartError?.message?.indexOf('No items') > -1
  const isLoading = isCheckoutCartLoading || isAvailableCreditsLoading || isDeliveryDropoffLoading

  return (
    <>
      <Card className={classes.card}>
        {isLoading && <SkeletonLoading />}

        {!isLoading && location?.address && (
          <MapPreview
            address={`${location.address.street} ${location.address.city} ${location.address.state} ${location.address.zip}`}
          />
        )}

        {!isLoading && (
          <Box padding={3}>
            <DeliveryDetails location={location} deliveryDropoff={deliveryDropoff} />

            <Divider />

            {checkoutCart && deliveryDropoff && (
              <CheckoutCart
                cart={checkoutCart}
                couponUsage={couponUsage}
                creditUsage={creditUsage}
                deleteMenuItem={removeItemFromCheckoutCartStart}
                isCartSaving={isCartSaving}
                dropoffId={deliveryDropoff.dropoffId}
                editMenuItem={loadEditOrder}
                isLoadingOrderForEdit={isLoadingOrderForEdit}
                isEditingPastOrder={false}
                programUsage={programUsage}
                selectMenuItem={selectMenuItem}
                storedCurrencyUsage={storedCurrencyUsage}
              />
            )}
          </Box>
        )}
      </Card>

      <Box marginY={2}>
        <Captcha />
      </Box>

      <ButtonCheckout
        disabled={checkoutCart?.orderItems?.length === 0}
        error={errorCheckingOut}
        isContinue={false}
        onClick={() => {
          // manually trigger a form submission from far away
          document.getElementById('checkoutButton').click()
        }}
        submitting={isCheckoutProcessing}
      />

      {checkoutCartError && (
        <CheckoutError serviceMessage={checkoutCartError.message} hasNoItem={hasNoItem} />
      )}

      {deliveryDropoff && location && (
        <Box marginY={3} textAlign="center">
          <Link
            onClick={() => {
              pushSnowplow(toOrderProcessSchema('daily', 'checkout_more_items_add'))
            }}
            to={formatUrl(placeOrderRoute.path, {
              dropoffId: deliveryDropoff.dropoffId,
              locationId: location.deliveryLocationId,
            })}
          >
            + Add More Items
          </Link>
        </Box>
      )}

      {normalizedMenuItem && menuItemForEdit && isEditingOrder && (
        <PersonalizeOrder
          addMoreIsHidden="true"
          dropoffId={deliveryDropoff.dropoffId}
          handleClose={handleClose}
          isEditingFromCheckout="true"
          isEditingOrder={isEditingOrder}
          locationId={location.deliveryLocationId}
          menuItemForEdit={menuItemForEdit}
          menuItemId={menuItemForEdit.menuItemId}
          normalizedMenuItem={normalizedMenuItem}
        />
      )}
    </>
  )
}

const SkeletonLoading = () => (
  <>
    <Skeleton height={150} variant="rect" />
    <Box padding={3}>
      <Skeleton height={30} width="80%" />
      <Skeleton height={30} width="80%" />
      <Box marginY={2}>
        <Skeleton height={40} width="90%" />
      </Box>
      <Skeleton height={50} />
    </Box>
  </>
)

const mapStateToProps = state => {
  const { errorCheckingOut, isCheckoutProcessing, isDeliveryLoading } = state.checkout
  const { normalizedMenuItem } = state.personalize
  const { deliveryDropoff, isDeliveryDropoffLoading } = state.delivery
  const { availableCredits, isAvailableCreditsLoading } = state.creditHistory
  const location = selectCurrentLocationWithAddress(state)
  const {
    checkoutCart,
    checkoutCartError,
    couponUsage,
    creditUsage,
    isCheckoutCartLoading,
    isCartSaving,
    isEditingOrder,
    isLoadingOrderForEdit,
    programUsage,
    storedCurrencyUsage,
  } = state.cart
  const menuItemForEdit = selectMenuItemForEdit(state)

  return {
    availableCredits,
    checkoutCart,
    checkoutCartError,
    errorCheckingOut,
    couponUsage,
    creditUsage,
    deliveryDropoff,
    isAvailableCreditsLoading,
    isCheckoutCartLoading,
    isCartSaving,
    isCheckoutProcessing,
    isDeliveryDropoffLoading,
    isDeliveryLoading,
    isEditingOrder,
    isLoadingOrderForEdit,
    location,
    menuItemForEdit,
    normalizedMenuItem,
    programUsage,
    storedCurrencyUsage,
  }
}

const mapDispatchToProps = {
  cancelEditingOrder,
  clearMenuItem,
  loadEditOrder,
  removeItemFromCheckoutCartStart,
  selectMenuItem,
}

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutSidebar)
